import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  CALL_TYPE_LABEL,
  OUTCOME_LABEL,
  OUTCOME_TONE,
  formatDuration,
  gapLabel,
  type CallType,
  type GradeCheckResult,
  type LeadJourneyResponse,
  type TimelineItem,
  type Tone,
} from '@coachrex/shared';
import { recordingUrl, useAddNote, useLeadJourney } from '../lib/api';
import { engagementTone, relativeStamp, scoreColor, stamp, toneClass } from '../lib/format';
import { Chip, ErrorBox, GradeBadge, Skeleton } from '../components/ui';
import { AddToRescueButton, ChangeGradeButton, RescueCard } from './JourneyActions';
import { AiHeardList } from '../components/AiHeard';
import './journey.css';

export function LeadJourney() {
  const { leadId = '' } = useParams();
  const { data, error, isLoading } = useLeadJourney(leadId);

  if (error) {
    return (
      <div className="page">
        <BackLink />
        <ErrorBox error={error} />
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="page">
        <BackLink />
        <Skeleton height={60} width={420} />
        <div className="journey-cols">
          <div className="journey-col">
            <Skeleton height={110} />
            <Skeleton height={120} />
            <Skeleton height={360} />
          </div>
          <div className="journey-col">
            <Skeleton height={220} />
            <Skeleton height={160} />
          </div>
        </div>
      </div>
    );
  }
  return <Journey data={data} />;
}

function BackLink() {
  return (
    <Link to="/conversations" style={{ fontSize: 13, alignSelf: 'flex-start' }}>
      ← All conversations
    </Link>
  );
}

function Journey({ data }: { data: LeadJourneyResponse }) {
  const { lead, timeZone } = data;
  const player = usePlayer(data);
  const meta = [lead.rep ? `Assigned to ${lead.rep.name}` : 'Unassigned', lead.source, lead.phone, lead.email].filter(Boolean).join(' · ');

  return (
    <div className="page">
      <BackLink />
      <div className="page-head">
        <div className="page-head__titles" style={{ gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className="h1">{lead.name}</h1>
            <Chip tone={OUTCOME_TONE[lead.outcome]} size="lg">
              {OUTCOME_LABEL[lead.outcome]}
            </Chip>
          </div>
          <span className="subtitle">{meta}</span>
        </div>
        <div className="head-actions">
          {/* Coach Lex never sends anything; reps act in the CRM. */}
          <a className="btn btn--secondary" href={lead.crmUrl} target="_blank" rel="noreferrer">
            Open in CRM
          </a>
          <AddToRescueButton data={data} />
        </div>
      </div>

      <div className="journey-cols">
        <div className="journey-col">
          <Milestones data={data} />
          <Stats data={data} />
          <Timeline data={data} player={player} />
          {data.call && <CallPanels data={data} player={player} />}
        </div>
        <div className="journey-col">
          <GradeCard data={data} />
          <RescueCard data={data} />
          {data.call && <CallScoreCard call={data.call} />}
          <section className="card stack-10">
            <span className="card-title">Lead summary</span>
            <p className="prose">{data.summary}</p>
          </section>
          <Notes leadId={lead.id} notes={data.notes} timeZone={timeZone} />
          <section className="card stack-10">
            <span className="card-title">Suggested next steps</span>
            {data.nextSteps.map((s, i) => (
              <div key={i} className="next-step">
                <span className="next-step__n">{i + 1}</span>
                <span>{s}</span>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}

// ---------- Left column ----------

function Milestones({ data }: { data: LeadJourneyResponse }) {
  const { items, reached, required } = data.milestones;
  return (
    <section className="card stack-12" style={{ padding: '16px 18px' }}>
      <div className="card-head">
        <span className="card-title">Milestones</span>
        <span className="card-meta" style={{ display: 'flex', gap: 8 }}>
          {items.length ? `${reached} of ${required} reached` : 'No milestones set up'}
          <Link to="/settings/journey">Edit</Link>
        </span>
      </div>
      {items.length > 0 && (
        <div className="milestones">
          {items.map((m) => (
            <span key={m.id} className={`milestone is-${m.state}${m.optional ? ' is-optional' : ''}`}>
              {m.state === 'reached' && <b>✓</b>}
              {m.state === 'next' && <b>→</b>}
              {m.name}
            </span>
          ))}
        </div>
      )}
      <AiHeardList items={data.milestones.heard} timeZone={data.timeZone} />
    </section>
  );
}

function Stats({ data }: { data: LeadJourneyResponse }) {
  const { calls, texts, emails, forms } = data.stats;
  const cells = [
    { label: 'Calls', s: calls },
    { label: 'Texts', s: texts },
    { label: 'Emails', s: emails },
  ];
  const e = data.engagement;
  return (
    <section className="card stats-strip">
      {cells.map((c) => (
        <div key={c.label} className="stat">
          <span className="stat__label">{c.label}</span>
          <span className="stat__value">{c.s.total}</span>
          <span className="stat__sub">
            {c.s.fromLead} from lead · {c.s.total - c.s.fromLead} from rep
          </span>
        </div>
      ))}
      <div className="stat">
        <span className="stat__label">Form fills</span>
        <span className="stat__value">{forms.total}</span>
        <span className="stat__sub">{forms.total ? 'From lead' : '—'}</span>
      </div>
      <div className="stat stat--eng">
        <span className="stat__label">Engagement</span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span className="stat__value">{e.score}</span>
          <Chip tone={engagementTone(e.label)} size="lg">
            {e.label}
          </Chip>
        </span>
        <span className="stat__sub">{e.why}</span>
      </div>
    </section>
  );
}

const CHANNEL_ICON = { call: 'Call', sms: 'SMS', email: 'Email', form: 'Form' } as const;

function Timeline({ data, player }: { data: LeadJourneyResponse; player: Player }) {
  const items = data.timeline;
  const trimmed = data.totalTouches - items.length;
  return (
    <section className="card stack-14">
      <div className="card-head">
        <span className="card-title">Journey</span>
        <span className="card-meta">{data.touchSummary}</span>
      </div>
      {trimmed > 0 && <span className="card-meta">Showing the latest {items.length} touches. {trimmed} earlier ones are in the CRM.</span>}
      {items.length === 0 ? (
        <span className="card-meta">No calls, texts or emails yet.</span>
      ) : (
        <ol className="timeline">
          {items.map((t, i) => (
            <TimelineEntry key={t.id} t={t} prev={items[i - 1]} last={i === items.length - 1} timeZone={data.timeZone} player={player} />
          ))}
        </ol>
      )}
    </section>
  );
}

function TimelineEntry({ t, prev, last, timeZone, player }: { t: TimelineItem; prev?: TimelineItem; last: boolean; timeZone: string; player: Player }) {
  const iconTone = t.channel === 'form' ? 'green' : t.direction === 'in' ? 'grey' : 'blue';
  const gap = prev ? gapLabel(prev.at, t.at) : null;
  const playing = player.touchId === t.id && player.playing;
  return (
    <li className={`tl-item${player.cued === t.id ? ' is-cued' : ''}`} id={`touch-${t.id}`}>
      <span className="tl-rail">
        <span className={`tl-icon ${toneClass(iconTone)}`}>{CHANNEL_ICON[t.channel]}</span>
        <span className="tl-line" style={{ opacity: last ? 0 : 1 }} />
      </span>
      <div className="tl-body">
        {gap && <span className={`tl-gap${gap.bold ? ' is-bold' : ''}`}>{gap.label}</span>}
        <div className="tl-title-row">
          <span className="tl-title">{t.title}</span>
          <span className="tl-meta">
            {t.by} · {stamp(t.at, timeZone)}
          </span>
          {t.flag && (
            <Chip tone={t.flag.tone} size="sm">
              {t.flag.label}
            </Chip>
          )}
        </div>
        {t.body && <span className={`tl-bubble${t.direction === 'in' ? ' is-lead' : ''}`}>{t.body}</span>}
        {t.callSummary && (
          <span className="tl-call-read">
            <Chip tone={CALL_TYPE_TONE[t.callSummary.type]} size="sm">
              {CALL_TYPE_LABEL[t.callSummary.type]}
            </Chip>
            <span>{t.callSummary.text}</span>
          </span>
        )}
        {t.channel === 'call' && t.hasRecording && (
          <button className="link" style={{ fontSize: 12.5, alignSelf: 'flex-start' }} onClick={() => player.toggle(t.id, t.durationSec ?? 0)}>
            {playing ? 'Pause recording' : 'Play recording'}
          </button>
        )}
      </div>
    </li>
  );
}

// ---------- Recording player ----------

interface Player {
  touchId: string | null;
  playing: boolean;
  pos: number;
  duration: number;
  error: string | null;
  toggle: (touchId?: string, durationSec?: number) => void;
  seek: (sec: number) => void;
  /** Set by a "Review call" link from Coaching: the call to highlight. */
  cued: string | null;
}

function usePlayer(data: LeadJourneyResponse): Player {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [touchId, setTouchId] = useState<string | null>(data.call?.hasRecording ? data.call.touchId : null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [duration, setDuration] = useState(data.call?.durationSec ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const cued = params.get('call');

  useEffect(() => {
    const a = new Audio();
    a.preload = 'none';
    a.ontimeupdate = () => setPos(a.currentTime);
    a.onplay = () => setPlaying(true);
    a.onpause = () => setPlaying(false);
    a.onended = () => setPlaying(false);
    a.onloadedmetadata = () => Number.isFinite(a.duration) && setDuration(a.duration);
    a.onerror = () => {
      setPlaying(false);
      setError('Recording unavailable');
    };
    audio.current = a;
    // Coaching links here with ?call=<touch>&t=<seconds>: load that recording at the moment (playback needs a click).
    const item = cued ? data.timeline.find((x) => x.id === cued) : undefined;
    if (item?.hasRecording) {
      a.src = recordingUrl(item.id);
      a.currentTime = Number(params.get('t')) || 0;
      setTouchId(item.id);
      setDuration(item.durationSec ?? 0);
      setPos(a.currentTime);
    }
    if (cued) setTimeout(() => document.getElementById(`touch-${cued}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    return () => {
      a.pause();
      a.src = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = (id: string, durationSec: number) => {
    const a = audio.current!;
    if (touchId !== id || !a.src) {
      a.src = recordingUrl(id);
      setTouchId(id);
      setDuration(durationSec);
      setPos(0);
      setError(null);
    }
    return a;
  };

  return {
    touchId,
    playing,
    pos,
    duration,
    error,
    toggle: (id = touchId ?? '', durationSec = duration) => {
      if (!id) return;
      const a = load(id, durationSec);
      if (playing && touchId === id) a.pause();
      else void a.play().catch(() => setError('Recording unavailable'));
    },
    cued,
    seek: (sec) => {
      if (!touchId) return;
      const a = load(touchId, duration);
      a.currentTime = Math.max(0, Math.min(sec, duration));
      setPos(a.currentTime);
      if (!playing) void a.play().catch(() => setError('Recording unavailable'));
    },
  };
}

/** Placeholder waveform: stable per call, not derived from the audio. */
function waveform(seed: string): number[] {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return Array.from({ length: 90 }, (_, i) => 22 + Math.round(Math.abs(Math.sin(i * 1.7 + (h % 7)) * 50 + Math.sin(i * 0.43 + (h % 13)) * 28)));
}

function CallPanels({ data, player }: { data: LeadJourneyResponse; player: Player }) {
  const call = data.call!;
  const activeId = player.touchId ?? call.touchId;
  const bars = useMemo(() => waveform(activeId), [activeId]);
  const progress = player.duration ? player.pos / player.duration : 0;
  const analysis = call.analysis;
  const notAnalyzed = call.notAnalyzed;

  return (
    <>
      {call.hasRecording && (
        <section className="card player">
          <button className="player__btn" onClick={() => player.toggle()} aria-label={player.playing ? 'Pause recording' : 'Play recording'}>
            {player.playing ? '❚❚' : '▶'}
          </button>
          <div
            className="player__wave"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(player.duration)}
            aria-valuenow={Math.round(player.pos)}
            tabIndex={0}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              player.seek(((e.clientX - r.left) / r.width) * player.duration);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') player.seek(player.pos + 5);
              if (e.key === 'ArrowLeft') player.seek(player.pos - 5);
            }}
          >
            {bars.map((h, i) => (
              <span key={i} style={{ height: `${h}%`, background: i / bars.length < progress ? 'var(--accent)' : '#d9dde6' }} />
            ))}
          </div>
          <span className="player__time">{player.error ?? `${formatDuration(player.pos)} / ${formatDuration(player.duration)}`}</span>
        </section>
      )}
      <section className="card stack-16">
        <div className="card-head">
          <span className="card-title">AI call summary</span>
          <span className="card-meta">{analysis ? 'Generated from the recording' : 'Not analyzed yet'}</span>
        </div>
        {analysis ? (
          <>
            <p className="prose" style={{ fontSize: 14 }}>
              {analysis.summary}
            </p>
            <div className="sum-groups">
              <SumGroup title="What the lead wanted" color="var(--accent)" items={analysis.wanted} />
              <SumGroup title="Objections" color="var(--amber-fg)" items={analysis.objections.length ? analysis.objections : ['None raised']} />
              <SumGroup title="Done well" color="var(--green-fg)" items={analysis.doneWell} />
              <SumGroup title="Missed" color="var(--red-fg)" items={analysis.missed} />
            </div>
            {analysis.keyMoments.length > 0 && (
              <div className="moments">
                <span className="overline" style={{ marginBottom: 4 }}>
                  Key moments
                </span>
                {analysis.keyMoments.map((m, i) => (
                  <button key={i} className="moment" onClick={() => player.seek(m.t)}>
                    <span className="moment__time">{formatDuration(m.t)}</span>
                    <span className="moment__text">{m.text}</span>
                    <Chip tone={m.tone}>{m.flag}</Chip>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="prose muted">{notAnalyzed ?? 'The summary, objections and key moments appear here once Coach Lex has analyzed this call.'}</p>
        )}
      </section>
    </>
  );
}

function SumGroup({ title, color, items }: { title: string; color: string; items: string[] }) {
  return (
    <div className="stack-8">
      <span className="overline">{title}</span>
      {items.map((t, i) => (
        <span key={i} className="sum-item">
          <span className="sum-dot" style={{ background: color }} />
          <span>{t}</span>
        </span>
      ))}
    </div>
  );
}

// ---------- Right column ----------

const CHECK: Record<GradeCheckResult, { mark: string; tone: 'green' | 'amber' | 'red' | 'grey' }> = {
  met: { mark: '✓', tone: 'green' },
  unconfirmed: { mark: '?', tone: 'amber' },
  found: { mark: '!', tone: 'red' },
  not_found: { mark: '–', tone: 'grey' },
};

function GradeCard({ data }: { data: LeadJourneyResponse }) {
  const g = data.grade;
  return (
    <section className="card stack-12">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <GradeBadge grade={g.final} size={48} />
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="card-title">Lead grade</span>
          <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{g.final ? g.reason : 'Not graded yet. Grades arrive once Coach Lex reads this lead’s calls and messages.'}</span>
        </span>
        <ChangeGradeButton data={data} />
      </div>
      {g.override && (
        <span className="override-note">
          AI graded {g.ai}. Changed from {g.override.from} to {g.override.to} by {g.override.by} · {relativeStamp(g.override.at, data.timeZone)}
        </span>
      )}
      {g.checks.length > 0 && (
        <div>
          {g.checks.map((k, i) => (
            <div key={i} className="check-row">
              <span className={`check-mark ${toneClass(CHECK[k.result].tone)}`}>{CHECK[k.result].mark}</span>
              <span style={{ flex: 1 }}>{k.text}</span>
              <span className="card-meta" style={{ whiteSpace: 'nowrap' }}>
                {k.group}
              </span>
            </div>
          ))}
        </div>
      )}
      <Link to="/settings/lead-scoring" style={{ fontSize: 12.5 }}>
        Edit scoring rules
      </Link>
    </section>
  );
}

const CALL_TYPE_TONE: Record<CallType, Tone> = {
  conversation: 'green',
  voicemail: 'amber',
  voice_agent: 'blue',
  no_conversation: 'grey',
};

function CallScoreCard({ call }: { call: NonNullable<LeadJourneyResponse['call']> }) {
  const unscored = call.analysis && call.analysis.callType !== 'conversation';
  return (
    <section className="card stack-14">
      <div className="card-head">
        <span className="card-title">Rep score</span>
        {call.score != null ? (
          <span className="call-score" style={{ color: scoreColor(call.score) }}>
            {call.score}
            <span>/100</span>
          </span>
        ) : (
          <span className="card-meta">{unscored ? `Not scored: ${CALL_TYPE_LABEL[call.analysis!.callType].toLowerCase()}` : 'Not scored yet'}</span>
        )}
      </div>
      {call.criteria.length > 0 && (
        <div>
          {call.criteria.map((k) => (
            <div key={k.name} className="check-row">
              <span className={`check-mark ${toneClass(k.pass ? 'green' : 'red')}`}>{k.pass ? '✓' : '✕'}</span>
              <span style={{ flex: 1 }}>{k.name}</span>
              <span className="card-meta">{k.weight}%</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Notes({ leadId, notes, timeZone }: { leadId: string; notes: LeadJourneyResponse['notes']; timeZone: string }) {
  const [draft, setDraft] = useState('');
  const add = useAddNote(leadId);
  const canSave = draft.trim().length > 0 && !add.isPending;
  const save = () => {
    if (!canSave) return;
    add.mutate(draft.trim(), { onSuccess: () => setDraft('') });
  };
  return (
    <section className="card stack-12">
      <div className="card-head" style={{ alignItems: 'center' }}>
        <span className="card-title">Notes</span>
        <span className="sync-label">
          <span className="sync-dot" />
          Two-way sync with HighLevel
        </span>
      </div>
      <textarea
        className="note-input"
        rows={2}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save();
        }}
        placeholder="Add a note. It will also appear on the contact in HighLevel."
        aria-label="New note"
      />
      {add.error && <span style={{ fontSize: 12.5, color: 'var(--red-fg)' }}>Couldn’t save the note: {add.error.message}</span>}
      <button className="btn btn--primary" style={{ alignSelf: 'flex-end', padding: '7px 14px', fontSize: 12.5 }} disabled={!canSave} onClick={save}>
        {add.isPending ? 'Saving…' : 'Add note'}
      </button>
      {notes.map((n) => (
        <div key={n.id} className="note">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{n.author}</span>
            <span className="card-meta" style={{ flex: 1 }}>
              {relativeStamp(n.at, timeZone)}
            </span>
            <Chip tone={n.source === 'crm' ? 'grey' : 'blue'} size="sm">
              {n.source === 'crm' ? 'From HighLevel' : 'Synced to HighLevel'}
            </Chip>
          </div>
          <span className="note__text">{n.text}</span>
        </div>
      ))}
      {notes.length === 0 && <span style={{ fontSize: 13, color: 'var(--faint)' }}>No notes yet. Notes added in HighLevel will show up here too.</span>}
    </section>
  );
}

